import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"

import { SEO } from "@/components/SEO"
import { CognitiveEnterpriseSchema } from "@/components/schema"
import { PageFAQ } from "@/components/PageFAQ"
import { cognitiveEnterpriseFAQs } from "@/data/faqData"

const challenges = [
    {
        num: "01",
        stat: "73%",
        statLabel: "of enterprises run disconnected core systems",
        title: "Fragmented Platforms",
        description: "Disparate ERP, CRM, and DXP systems create data silos that hinder unifying business logic across the organization.",
        painPoints: ["Data silos", "Duplicate processes", "No single source of truth"],
    },
    {
        num: "02",
        stat: "2.5x",
        statLabel: "longer time-to-market with legacy stacks",
        title: "Legacy Innovation Gap",
        description: "Outdated architectures slow down adoption of modern digital features and AI capabilities, leaving enterprises behind competitors.",
        painPoints: ["Slow releases", "Rigid architecture", "AI-blocked"],
    },
    {
        num: "03",
        stat: "58%",
        statLabel: "of business decisions lack real-time data",
        title: "Disconnected Intelligence",
        description: "Manual processes and limited real-time visibility across business functions reduce decision quality and responsiveness.",
        painPoints: ["Batch reporting", "No automation", "Blind spots"],
    },
]

const approachSteps = [
    {
        num: "01",
        title: "Incremental Modernization",
        description: "Upgrading ERP, CRM, and DXP platforms strategically to deliver value continuously without major disruption or rip-and-replace.",
    },
    {
        num: "02",
        title: "Integration & Orchestration",
        description: "Connecting systems via APIs and event-driven architectures to form a cohesive, agile ecosystem with real-time data flow.",
    },
    {
        num: "03",
        title: "Unified Data & AI Foundation",
        description: "Creating a single source of truth and layering AI to automate workflows, enhance decisioning, and drive predictive insights.",
    },
]

const deliverableRows = [
    [
        "ERP & Core Systems Modernization",
        "CRM & Customer Platforms",
        "Digital Experience Platforms",
        "Data Platforms & Analytics",
        "Integration & Orchestration",
        "Intelligent Workflow Automation",
        "API Strategy & Management",
        "Master Data Management",
    ],
    [
        "SAP & Oracle Modernization",
        "Salesforce & Dynamics 365",
        "Adobe Experience Cloud",
        "Event-Driven Architecture",
        "iPaaS & Middleware",
        "Process Mining & Optimization",
        "AI-Powered Decision Support",
        "Legacy Migration & Replatforming",
    ],
    [
        "Composable Architecture",
        "Microservices Decomposition",
        "Real-Time Analytics Pipelines",
        "Customer 360 Platforms",
        "Supply Chain Intelligence",
        "Document Intelligence & OCR",
        "Low-Code Citizen Development",
        "Enterprise Knowledge Graphs",
    ],
]

const CognitiveEnterprise = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Enterprise Systems Built for Intelligent Operations | RSA Tech Group"
                description="We modernize, integrate, and optimize core enterprise platforms to drive agility and intelligence without disruptive rip-and-replace cycles."
                image="https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=2670&auto=format&fit=crop"
            />
            <CognitiveEnterpriseSchema />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-24 overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_30%,rgba(28,43,110,0.3),transparent_60%)]" />
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#1C2B6E]/10 rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FDA400]/[0.04] rounded-full blur-[120px]" />

                <div className="section-container relative z-10 text-center">
                    <div className="max-w-4xl mx-auto">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                            Cognitive Enterprise
                        </p>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight">
                            Enterprise Systems Built for{" "}
                            <span className="text-[#FDA400]">Intelligent Operations</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-12 font-light leading-relaxed">
                            We modernize, integrate, and optimize core enterprise platforms to drive agility and intelligence without disruptive rip-and-replace cycles.
                        </p>
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                        >
                            Talk to an Enterprise Systems Expert <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                    </div>
                </div>
            </div>

            {/* The Challenge */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.2]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            The Problem
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            The Enterprise Systems{" "}
                            <span className="text-[#FDA400]">Challenge</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-3xl mx-auto font-light leading-relaxed">
                            Most enterprises are held back by disconnected systems that can't keep up with the speed of modern business.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
                        {challenges.map((item) => (
                            <div
                                key={item.num}
                                className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2"
                            >
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />
                                <div className="absolute -top-16 -right-16 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/[0.10] group-hover:scale-125 transition-all duration-700" />
                                <span className="absolute top-6 right-8 text-[120px] font-black text-[#1C2B6E]/[0.03] leading-none select-none group-hover:text-[#1C2B6E]/[0.06] transition-colors duration-500">
                                    {item.num}
                                </span>

                                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                                    <div className="mb-6">
                                        <div className="text-5xl md:text-6xl font-black text-[#FDA400] leading-none mb-1">
                                            {item.stat}
                                        </div>
                                        <div className="text-[10px] text-slate-400 uppercase tracking-[0.15em] font-semibold">
                                            {item.statLabel}
                                        </div>
                                    </div>

                                    <h3 className="text-2xl font-black text-[#1C2B6E] tracking-tight mb-3">
                                        {item.title}
                                    </h3>
                                    <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                                        {item.description}
                                    </p>

                                    <div className="flex flex-wrap gap-2 mt-auto pt-6 border-t border-slate-100">
                                        {item.painPoints.map((point, i) => (
                                            <span
                                                key={i}
                                                className="px-3 py-1.5 bg-[#1C2B6E]/[0.04] text-[#1C2B6E]/60 text-xs font-medium rounded-full border border-[#1C2B6E]/[0.06] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default"
                                            >
                                                {point}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Strategy-Led Approach */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
                        <div>
                            <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                                Our Methodology
                            </p>
                            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                                Strategy-Led{" "}
                                <span className="text-[#FDA400]">Enterprise</span>{" "}
                                Approach
                            </h2>
                            <p className="text-slate-400 text-lg font-light leading-relaxed mb-10">
                                We modernize core platforms incrementally, connect them into a cohesive ecosystem, and layer AI to unlock intelligent operations - without disrupting what already works.
                            </p>

                            <div className="space-y-8">
                                {approachSteps.map((step) => (
                                    <div key={step.num} className="flex gap-5 group">
                                        <div className="w-12 h-12 bg-[#FDA400]/10 border border-[#FDA400]/20 rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-sm font-black text-[#FDA400]">{step.num}</span>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-black text-white tracking-tight mb-2">{step.title}</h3>
                                            <p className="text-slate-400 text-sm font-light leading-relaxed">{step.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Enterprise Architecture Visualization */}
                        <div className="relative">
                            <div className="absolute inset-0 bg-[#FDA400]/[0.03] rounded-3xl blur-[60px]" />
                            <div className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10">
                                <div className="flex items-center justify-between mb-8 border-b border-white/10 pb-4">
                                    <div className="text-xs text-slate-400 uppercase tracking-[0.2em] font-bold">Enterprise Architecture</div>
                                    <div className="flex items-center gap-2">
                                        <span className="relative flex h-2 w-2">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FDA400] opacity-40" />
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FDA400]" />
                                        </span>
                                        <span className="text-[#FDA400] text-xs font-bold">Unified</span>
                                    </div>
                                </div>

                                {/* Architecture Layers */}
                                <div className="space-y-4">
                                    {/* AI Layer */}
                                    <div className="bg-[#FDA400]/10 border border-[#FDA400]/20 rounded-2xl p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-[#FDA400]/20 rounded-lg flex items-center justify-center">
                                                <span className="text-[#FDA400] text-xs font-black">AI</span>
                                            </div>
                                            <span className="text-white text-sm font-bold">Intelligent Operations Layer</span>
                                        </div>
                                        <div className="w-2 h-2 rounded-full bg-[#FDA400] animate-pulse" />
                                    </div>

                                    {/* Connection */}
                                    <div className="flex justify-center">
                                        <div className="w-px h-6 bg-gradient-to-b from-[#FDA400]/40 to-[#1C2B6E]/40" />
                                    </div>

                                    {/* Integration Layer */}
                                    <div className="bg-[#1C2B6E]/20 border border-[#1C2B6E]/30 rounded-2xl p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-[#1C2B6E]/30 rounded-lg flex items-center justify-center">
                                                <span className="text-white text-xs font-black">API</span>
                                            </div>
                                            <span className="text-white text-sm font-bold">Integration & Data Fabric</span>
                                        </div>
                                    </div>

                                    {/* Connection */}
                                    <div className="flex justify-center">
                                        <div className="w-px h-6 bg-gradient-to-b from-[#1C2B6E]/40 to-white/10" />
                                    </div>

                                    {/* Core Systems */}
                                    <div className="grid grid-cols-3 gap-3">
                                        {["ERP", "CRM", "DXP"].map((sys) => (
                                            <div key={sys} className="bg-white/[0.06] border border-white/[0.08] rounded-xl p-3 text-center hover:bg-white/[0.10] hover:border-[#FDA400]/20 transition-all duration-300 group/sys cursor-default">
                                                <span className="text-xs font-black text-slate-400 group-hover/sys:text-[#FDA400] transition-colors">{sys}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-8 pt-6 border-t border-white/10 grid grid-cols-3 gap-4">
                                    {[
                                        { value: "15+", label: "Systems Unified" },
                                        { value: "4.2x", label: "Faster Decisions" },
                                        { value: "360°", label: "Data Visibility" },
                                    ].map((stat, i) => (
                                        <div key={i} className="text-center">
                                            <div className="text-lg font-black text-[#FDA400]">{stat.value}</div>
                                            <div className="text-[9px] text-slate-500 uppercase tracking-wider font-semibold">{stat.label}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What We Deliver - Scrolling Marquee */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.15]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Capabilities
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            What We{" "}
                            <span className="text-[#FDA400]">Deliver</span>
                        </h2>
                        <p className="text-slate-500 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
                            End-to-end enterprise modernization designed to unify platforms, embed intelligence, and drive operational agility.
                        </p>
                    </div>
                </div>

                <div className="relative z-10 space-y-4">
                    <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white via-white/80 to-transparent z-20 pointer-events-none" />
                    <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white via-white/80 to-transparent z-20 pointer-events-none" />

                    {deliverableRows.map((row, rowIndex) => (
                        <div key={rowIndex} className="overflow-hidden">
                            <div
                                className="flex gap-4 w-max"
                                style={{
                                    animation: `${rowIndex % 2 === 0 ? "scrollLeft" : "scrollRight"} ${30 + rowIndex * 5}s linear infinite`,
                                }}
                            >
                                {[...row, ...row].map((item, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-3 px-6 py-4 bg-white rounded-2xl border border-[#1C2B6E]/[0.06] shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:border-[#FDA400]/30 hover:shadow-[0_12px_40px_rgba(28,43,110,0.1)] transition-all duration-300 group cursor-default flex-shrink-0"
                                    >
                                        <span className="relative flex h-2 w-2 flex-shrink-0">
                                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#FDA400] opacity-30 group-hover:animate-ping" />
                                            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FDA400]" />
                                        </span>
                                        <span className="text-[#1C2B6E] font-bold text-sm whitespace-nowrap group-hover:text-[#FDA400] transition-colors duration-300">
                                            {item}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Enterprise Ready + Engagement Model */}
            <section className="py-24 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.15),transparent_70%)]" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.03] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    {/* Enterprise Ready */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Built for Scale
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
                            Enterprise Ready{" "}
                            <span className="text-[#FDA400]">by Design</span>
                        </h2>
                        <p className="text-slate-400 text-lg mt-6 max-w-2xl mx-auto font-light leading-relaxed">
                            Architectures designed to support global enterprises with distributed operations, high availability, and strict compliance.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-24">
                        {[
                            { num: "01", title: "Global Scale Architecture", desc: "Designed for distributed operations across regions, time zones, and regulatory environments with high availability.", tags: ["Multi-Region", "HA/DR", "Edge-Ready", "Global CDN"] },
                            { num: "02", title: "Secure & Compliant", desc: "Built with robust security protocols to align with your existing enterprise governance and compliance standards.", tags: ["SOC2", "GDPR", "HIPAA", "ISO 27001"] },
                        ].map((item) => (
                            <div
                                key={item.num}
                                className="relative bg-white/[0.04] backdrop-blur-sm border border-white/[0.08] rounded-3xl p-8 md:p-10 hover:bg-white/[0.07] hover:border-[#FDA400]/20 transition-all duration-500 group"
                            >
                                <span className="absolute top-4 right-6 text-7xl font-black text-white/[0.03] leading-none select-none group-hover:text-white/[0.06] transition-colors duration-500">
                                    {item.num}
                                </span>
                                <div className="relative z-10">
                                    <div className="text-xs font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-4">
                                        {item.num}
                                    </div>
                                    <h3 className="text-2xl font-black text-white tracking-tight mb-3">{item.title}</h3>
                                    <p className="text-slate-400 text-sm font-light leading-relaxed mb-6">{item.desc}</p>
                                    <div className="flex flex-wrap gap-2 pt-5 border-t border-white/[0.06]">
                                        {item.tags.map((tag, i) => (
                                            <span key={i} className="px-3 py-1.5 bg-white/[0.04] text-slate-400 text-xs font-medium rounded-full border border-white/[0.08] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Engagement Model */}
                    <div className="text-center mb-16">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            Our Process
                        </p>
                        <h2 className="text-3xl md:text-4xl font-black text-white tracking-tight leading-[1.1]">
                            Strategy-Led{" "}
                            <span className="text-[#FDA400]">Engagement</span>
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8 relative max-w-5xl mx-auto">
                        <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-[#FDA400]/40 via-[#1C2B6E]/30 to-[#FDA400]/40 z-0" />

                        {[
                            { num: "01", title: "Assess & Align", desc: "Evaluate your current system landscape and align modernization opportunities with business goals." },
                            { num: "02", title: "Design & Modernize", desc: "Design and implement integrated architectures that modernize core systems iteratively." },
                            { num: "03", title: "Operate & Optimize", desc: "Provide ongoing support and optimization to ensure your enterprise intelligence scales with you." },
                        ].map((step) => (
                            <div key={step.num} className="relative z-10 text-center">
                                <div className="w-28 h-28 mx-auto bg-[#0f1629] border-2 border-[#FDA400]/30 rounded-full flex items-center justify-center mb-8 shadow-[0_0_30px_rgba(253,164,0,0.1)]">
                                    <span className="text-3xl font-black text-[#FDA400]">{step.num}</span>
                                </div>
                                <h3 className="text-xl font-black text-white tracking-tight mb-3">
                                    {step.title}
                                </h3>
                                <p className="text-slate-400 text-sm font-light leading-relaxed max-w-xs mx-auto">
                                    {step.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <PageFAQ
                faqs={cognitiveEnterpriseFAQs}
                title="Cognitive Enterprise FAQs"
                subtitle="Common questions about building intelligent, AI-driven organizations"
                variant="muted"
            />

            {/* CTA */}
            <section className="py-28 relative overflow-hidden bg-[#0f1629]">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_50%,rgba(28,43,110,0.2),transparent_70%)]" />
                <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] bg-[#FDA400]/[0.04] rounded-full blur-[150px]" />

                <div className="section-container relative z-10 text-center">
                    <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                        Get Started
                    </p>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-6">
                        Modernize Your Enterprise{" "}
                        <span className="text-[#FDA400]">Without Disruption</span>
                    </h2>
                    <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto font-light leading-relaxed">
                        Partner with RSA Tech Group to unlock intelligent operations across your entire organization.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-[#1C2B6E] bg-[#FDA400] rounded-full hover:bg-[#f5b800] transition-all duration-300 shadow-[0_8px_30px_rgba(253,164,0,0.3)] hover:shadow-[0_12px_40px_rgba(253,164,0,0.4)] hover:-translate-y-0.5"
                    >
                        Talk to an Enterprise Systems Expert <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    )
}

export default CognitiveEnterprise
